
import { useState, useEffect, useMemo } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { isOfflineError } from '../../lib/firestore';
import { INSIGHT_TYPE, IS_MOCK_DEMO } from '../../constants';
import { getDuplicateEntityPool } from '../../lib/health';
import { getInsightReviewMeta, isHintInsight, isProposedChangeInsight } from '../../lib/utils';
import MarkdownBlock from '../LazyMarkdownBlock';
import TagEditor from '../TagEditor';

const DUPLICATE_ENTITY_LABELS = {
  note: 'Note',
  asset: 'Asset',
  insight: 'Hint',
};

const PATCH_FIELD_LABELS = {
  title: 'タイトル',
  body: '内容',
  summary: '要約',
  tags: 'タグ',
  oldTag: '変更前タグ',
  newTag: '変更後タグ',
};

function getDuplicatePreviewText(item, type) {
  if (!item) return '';
  if (type === 'insight') return item.summary || item.body || '';
  return item.body || item.summary || item.text || '';
}

function getDuplicateEditableText(item, type) {
  if (!item) return '';
  if (type === 'insight') return item.summary || '';
  if (type === 'note' || type === 'asset') return item.body || '';
  return getDuplicatePreviewText(item, type);
}

function normalizeTagList(tags) {
  return Array.from(new Set((tags || []).map(tag => String(tag).trim()).filter(Boolean)));
}

function areTagListsEqual(a, b) {
  const left = normalizeTagList(a);
  const right = normalizeTagList(b);
  return left.length === right.length && left.every((tag, index) => tag === right[index]);
}

function getDuplicateActionButtonLabel(sourceActionLabel, variant = 'header') {
  if (variant === 'confirm') return '提案を受理';
  if (sourceActionLabel === '破棄') return '提案を受理';
  if (sourceActionLabel === 'アーカイブ') return '提案を受理';
  return '提案を受理';
}

function getDuplicateTextLabel(targetType) {
  if (targetType === 'insight') return 'Summary';
  if (targetType === 'note') return '本文';
  if (targetType === 'asset') return '本文';
  return '内容';
}

export default function InsightDetail({
  selectedInsight,
  setSelectedInsightId,
  openAssetTypeModal,
  setRejectingInsightId,
  setRejectReason,
  setIsRejectModalOpen,
  handleUpdateInsightTags,
  handleUpdateInsightTitle,
  handleUpdateInsightSummary,
  handleUpdateInsightMemo,
  handleUpdateInsightPatch,
  handleUpdateNoteTags,
  handleUpdateNoteBody,
  handleUpdateAssetTags,
  handleUpdateAssetBody,
  openSourceRoom,
  jumpToMap,
  handleDiscussInSink,
  onAcceptProposal,
  isAcceptingProposal,
  handleAcceptDuplicateProposal,
  isProcessingDuplicate,
  tagSuggestions = [],
  showToast,
  onReferenceClick,
  notes = [],
  concepts = [],
  insights = [],
  chatRooms = [],
}) {
  const rejectionMemo = selectedInsight?.rejectionMemo ?? selectedInsight?.metadata?.rejectReason ?? '';
  const [isEditingMemo, setIsEditingMemo] = useState(false);
  const [editingMemo, setEditingMemo] = useState(rejectionMemo);

  const [prevId, setPrevId] = useState(selectedInsight?.id);
  const [prevMemo, setPrevMemo] = useState(rejectionMemo);

  const [localTitle, setLocalTitle] = useState(selectedInsight?.title || '');
  const [isSummaryEditing, setIsSummaryEditing] = useState(false);
  const [localSummary, setLocalSummary] = useState(selectedInsight?.summary || '');

  const [isPatchEditing, setIsPatchEditing] = useState(false);
  const [localPatch, setLocalPatch] = useState(selectedInsight?.proposedPatch || {});
  const [duplicateDraftKey, setDuplicateDraftKey] = useState('');
  const [duplicateDraftTags, setDuplicateDraftTags] = useState([]);
  const [duplicateDraftSummary, setDuplicateDraftSummary] = useState('');
  const [isSavingDuplicateTarget, setIsSavingDuplicateTarget] = useState(false);

  const [lazySourceNote, setLazySourceNote] = useState(null);
  const [lazySourceRoom, setLazySourceRoom] = useState(null);
  const [lazyRelatedAssets, setLazyRelatedAssets] = useState([]);
  const [isLoadingRefs, setIsLoadingRefs] = useState(false);
  const suggestedTargetType = selectedInsight?.suggestedTargetType;
  const isActionHint = suggestedTargetType === 'todo' || suggestedTargetType === 'project';
  const actionTargetLabel = suggestedTargetType === 'project' ? 'Project' : 'ToDo';

  if (selectedInsight?.id !== prevId || rejectionMemo !== prevMemo) {
    setEditingMemo(rejectionMemo);
    setIsEditingMemo(false);
    setLocalTitle(selectedInsight?.title || '');
    setLocalSummary(selectedInsight?.summary || '');
    setIsSummaryEditing(false);
    setLocalPatch(selectedInsight?.proposedPatch || {});
    setIsPatchEditing(false);
    setPrevId(selectedInsight?.id);
    setPrevMemo(rejectionMemo);
  }

  useEffect(() => {
    if (!selectedInsight) return;

    const fetchRefs = async () => {
      setIsLoadingRefs(true);
      try {
        const noteId = selectedInsight?.sourceNoteId || (selectedInsight?.sourceNoteIds && selectedInsight?.sourceNoteIds[0]);
        const insightRoomId = selectedInsight?.sourceRoomId || (selectedInsight?.sourceRoomIds && selectedInsight?.sourceRoomIds[0]);
        const assetIds = selectedInsight?.relatedConceptIds || (selectedInsight?.conceptId ? [selectedInsight?.conceptId] : []);

        let noteDoc = null;
        let roomDoc = null;
        let assetDocs = [];

        if (IS_MOCK_DEMO) {
          noteDoc = notes.find(n => n.id === noteId);
          const effectiveRoomId = insightRoomId || noteDoc?.sourceRoomId || (noteDoc?.sourceRoomIds && noteDoc?.sourceRoomIds[0]);
          roomDoc = chatRooms.find(r => r.id === effectiveRoomId);
          assetDocs = assetIds.map(id => concepts.find(c => c.id === id)).filter(Boolean);
        } else {
          // Real Firebase Fetching
          if (noteId) {
            const nSnap = await getDoc(doc(db, 'notes', noteId));
            if (nSnap.exists()) noteDoc = { id: nSnap.id, ...nSnap.data() };
          }

          const effectiveRoomId = insightRoomId || noteDoc?.sourceRoomId || (noteDoc?.sourceRoomIds && noteDoc?.sourceRoomIds[0]);
          if (effectiveRoomId) {
            const rSnap = await getDoc(doc(db, 'chatRooms', effectiveRoomId));
            if (rSnap.exists()) roomDoc = { id: rSnap.id, ...rSnap.data() };
          }

          if (assetIds.length > 0) {
            const snaps = await Promise.all(assetIds.map(id => getDoc(doc(db, 'concepts', id))));
            assetDocs = snaps.filter(s => s.exists()).map(s => ({ id: s.id, ...s.data() }));
          }
        }

        setLazySourceNote(noteDoc);
        setLazySourceRoom(roomDoc);
        setLazyRelatedAssets(assetDocs);
      } catch (error) {
        if (isOfflineError(error)) {
          console.warn('Failed to fetch insight references: client is offline.');
        } else {
          console.error('Failed to fetch insight references:', error);
        }
      } finally {
        setIsLoadingRefs(false);
      }
    };

    fetchRefs();
  }, [selectedInsight, notes, concepts, chatRooms]);

  const handleSaveMemo = async () => {
    await handleUpdateInsightMemo(selectedInsight?.id, editingMemo);
    setIsEditingMemo(false);
  };

  const handleTitleBlur = () => {
    if (localTitle !== selectedInsight?.title) {
      handleUpdateInsightTitle(selectedInsight?.id, localTitle);
    }
  };

  const handleTitleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.target.blur();
    }
  };

  const handleSavePatch = async () => {
    await handleUpdateInsightPatch(selectedInsight?.id, localPatch);
    setIsPatchEditing(false);
  };

  const handleUpdatePatchField = (key, value) => {
    setLocalPatch(prev => ({ ...prev, [key]: value }));
  };

  const formatRoomName = (room) => {
    if (!room) return '(Unknown Room)';
    if (room.title) return room.title;
    try {
      const date = room.createdAt?.toDate ? room.createdAt.toDate() : new Date(room.createdAt);
      return date.toLocaleString();
    } catch {
      return '(無題のRoom)';
    }
  };

  const duplicateInsightPreview = useMemo(() => {
    if (!selectedInsight?.type?.startsWith('duplicate')) return null;

    const targetType = selectedInsight?.targetType;
    const entityPool = getDuplicateEntityPool(targetType, { notes, concepts, insights });
    const sourceId = selectedInsight?.proposedPatch?.mergeFrom;
    const target = entityPool.find(item => item.id === selectedInsight?.targetId) || null;
    const source = entityPool.find(item => item.id === sourceId) || null;
    const sourceOnlyTags = (source?.tags || []).filter(tag => !(target?.tags || []).includes(tag));
    const targetPreviewText = getDuplicatePreviewText(target, targetType);
    const sourcePreviewText = getDuplicatePreviewText(source, targetType);
    const reviewItems = [];

    if (sourceOnlyTags.length > 0) {
      reviewItems.push({
        label: '追加候補のタグ',
        kind: 'tags',
        value: sourceOnlyTags,
      });
    }

    if (sourcePreviewText && sourcePreviewText !== targetPreviewText) {
      reviewItems.push({
        label: '追加候補の内容',
        kind: 'text',
        value: sourcePreviewText,
      });
    }

    return {
      entityLabel: DUPLICATE_ENTITY_LABELS[targetType] || '項目',
      sourceActionLabel: targetType === 'insight' ? '破棄' : 'アーカイブ',
      target,
      source,
      sourceId,
      reviewItems,
      targetPreviewText,
      sourcePreviewText,
    };
  }, [selectedInsight, notes, concepts, insights]);

  const duplicateTarget = duplicateInsightPreview?.target;
  const canEditDuplicateTarget = Boolean(
    duplicateInsightPreview?.target && (
      selectedInsight?.targetType === 'insight' ||
      (selectedInsight?.targetType === 'note' && handleUpdateNoteTags && handleUpdateNoteBody) ||
      (selectedInsight?.targetType === 'asset' && handleUpdateAssetTags && handleUpdateAssetBody)
    )
  );
  const duplicateTagReviewItem = duplicateInsightPreview?.reviewItems?.find(item => item.kind === 'tags');
  const duplicateTextReviewItem = duplicateInsightPreview?.reviewItems?.find(item => item.kind === 'text');
  const duplicateTextLabel = getDuplicateTextLabel(selectedInsight?.targetType);
  const duplicateMergedTags = normalizeTagList([
    ...(duplicateTarget?.tags || []),
    ...(duplicateTagReviewItem?.value || []),
  ]);
  const duplicateTargetSummary = getDuplicateEditableText(duplicateTarget, selectedInsight?.targetType);
  const duplicateMergedSummary = duplicateTextReviewItem?.value && !duplicateTargetSummary.includes(duplicateTextReviewItem.value)
    ? `${duplicateTargetSummary.trim() ? `${duplicateTargetSummary.trim()}\n\n` : ''}${duplicateTextReviewItem.value}`
    : duplicateTargetSummary;
  const duplicateTargetTagsChanged = canEditDuplicateTarget && !areTagListsEqual(duplicateDraftTags, duplicateTarget?.tags || []);
  const duplicateTargetSummaryChanged = canEditDuplicateTarget && duplicateDraftSummary !== duplicateTargetSummary;
  const duplicateTargetHasChanges = duplicateTargetTagsChanged || duplicateTargetSummaryChanged;
  const duplicateDraftMatchesMerged = canEditDuplicateTarget && areTagListsEqual(duplicateDraftTags, duplicateMergedTags) && duplicateDraftSummary === duplicateMergedSummary;

  const nextDuplicateDraftKey = duplicateTarget ? `${selectedInsight?.id || ''}:${duplicateTarget.id || selectedInsight?.targetId || ''}` : '';
  if (duplicateDraftKey !== nextDuplicateDraftKey) {
    setDuplicateDraftKey(nextDuplicateDraftKey);
    setDuplicateDraftTags(duplicateMergedTags);
    setDuplicateDraftSummary(duplicateMergedSummary);
    setIsSavingDuplicateTarget(false);
  }

  const handleResetDuplicateDraft = () => {
    setDuplicateDraftTags(duplicateMergedTags);
    setDuplicateDraftSummary(duplicateMergedSummary);
  };

  const handleSaveDuplicateTarget = async () => {
    if (!canEditDuplicateTarget || !duplicateTarget?.id || !duplicateTargetHasChanges) return;
    setIsSavingDuplicateTarget(true);
    try {
      const tasks = [];
      if (duplicateTargetTagsChanged) {
        if (selectedInsight?.targetType === 'insight') {
          tasks.push(handleUpdateInsightTags(duplicateTarget.id, normalizeTagList(duplicateDraftTags)));
        } else if (selectedInsight?.targetType === 'note') {
          tasks.push(handleUpdateNoteTags(duplicateTarget.id, normalizeTagList(duplicateDraftTags)));
        } else if (selectedInsight?.targetType === 'asset') {
          tasks.push(handleUpdateAssetTags(duplicateTarget.id, normalizeTagList(duplicateDraftTags)));
        }
      }
      if (duplicateTargetSummaryChanged) {
        if (selectedInsight?.targetType === 'insight') {
          tasks.push(handleUpdateInsightSummary(duplicateTarget.id, duplicateDraftSummary));
        } else if (selectedInsight?.targetType === 'note') {
          tasks.push(handleUpdateNoteBody(duplicateTarget.id, duplicateDraftSummary));
        } else if (selectedInsight?.targetType === 'asset') {
          tasks.push(handleUpdateAssetBody(duplicateTarget.id, duplicateDraftSummary));
        }
      }
      await Promise.all(tasks);
      showToast?.('残す内容を保存しました。');
    } catch (error) {
      console.error('Failed to update duplicate target:', error);
      showToast?.('保存に失敗しました。');
    } finally {
      setIsSavingDuplicateTarget(false);
    }
  };

  if (!selectedInsight) return null;

  const isSuggested = selectedInsight.status === 'suggested';
  const isProposedChange = isProposedChangeInsight(selectedInsight);
  const isDuplicateProposal = isProposedChange && selectedInsight.type?.startsWith('duplicate');
  const showDuplicateActionBar = isSuggested && isDuplicateProposal;
  const tagMergeOldTag = selectedInsight.proposedPatch?.oldTag ?? selectedInsight.proposedPatch?.from;
  const tagMergeNewTag = selectedInsight.proposedPatch?.newTag ?? selectedInsight.proposedPatch?.to;
  const reviewMeta = getInsightReviewMeta(selectedInsight);

  return (
    <div className={`flex-1 min-h-0 overflow-y-auto overscroll-contain flex flex-col px-4 space-y-4 relative ${showDuplicateActionBar ? 'pb-32 lg:pb-0' : 'pb-4'}`}>
      <div className="sticky top-0 z-10 bg-gray-900/95 backdrop-blur py-2 -mx-4 px-4 flex justify-between items-center border-b border-gray-800/50">
        <button onClick={() => setSelectedInsightId(null)} className="text-sm text-blue-400 hover:text-blue-300 font-bold flex items-center gap-1">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" /></svg>
          一覧
        </button>
        <div className="flex items-center gap-1.5">
          {selectedInsight?.status === 'suggested' && !showDuplicateActionBar && (
            <button
              onClick={() => { setRejectingInsightId(selectedInsight?.id); setRejectReason(''); setIsRejectModalOpen(true); }}
              title="却下"
              className="p-2 rounded-lg border border-red-700/50 bg-red-950/40 text-red-400 hover:bg-red-600 hover:text-white hover:border-red-500 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
          {handleDiscussInSink && (
            <button
              onClick={() => handleDiscussInSink('insight', selectedInsight?.id, selectedInsight?.title)}
              title="Sinkで議論"
              className="p-2 rounded-lg border border-indigo-700/50 bg-indigo-950/40 text-indigo-400 hover:bg-indigo-600 hover:text-white hover:border-indigo-500 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
              </svg>
            </button>
          )}
          {jumpToMap && (
            <button
              onClick={() => jumpToMap(`insight:${selectedInsight?.id}`, selectedInsight?.title)}
              title="Mapで確認"
              className="p-2 rounded-lg border border-slate-700/50 bg-slate-900/40 text-slate-400 hover:bg-slate-700 hover:text-white hover:border-slate-500 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 20l-5.447-2.724A2 2 0 013 15.488V5.113a2 2 0 011.134-1.789L9 1m0 19l6 3m-6-3V1m6 22l5.447-2.724A2 2 0 0021 18.512V8.113a2 2 0 00-1.134-1.789L15 4m0 19V4m0 0L9 1" />
              </svg>
            </button>
          )}
        </div>
      </div>
      <article className="space-y-6">
        <header>
          <div className="flex items-center gap-2 mb-1.5">
            <span className={`text-[10px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded border ${
              isProposedChange
                ? 'bg-indigo-950/40 text-indigo-300 border-indigo-900/50'
                : 'bg-emerald-950/40 text-emerald-300 border-emerald-900/50'
            }`}>
              {reviewMeta.label}
            </span>
            <span className="text-[9px] font-bold uppercase tracking-widest text-purple-400 border border-purple-900/50 px-1.5 py-0.5 rounded">{selectedInsight?.status}</span>
            {selectedInsight?.metadata?.parseFailed && (
              <span className="bg-red-950/40 text-red-400 text-[9px] font-black border border-red-900/50 px-1.5 py-0.5 rounded animate-pulse">
                ⚠ パース失敗
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 group/title">
            <input
              type="text"
              value={localTitle}
              onChange={(e) => setLocalTitle(e.target.value)}
              onBlur={handleTitleBlur}
              onKeyDown={handleTitleKeyDown}
              className="flex-1 bg-transparent text-xl font-bold text-white leading-tight focus:outline-none focus:ring-1 focus:ring-purple-500/30 rounded px-1 -ml-1 border-none"
              placeholder="タイトルを入力..."
            />
            <svg className="w-3.5 h-3.5 text-gray-600 opacity-0 group-hover/title:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
            </svg>
          </div>
          <div className="mt-3">
            <TagEditor
              tags={selectedInsight?.tags || []}
              onUpdate={(newTags) => handleUpdateInsightTags(selectedInsight?.id, newTags)}
              suggestions={tagSuggestions}
              showToast={showToast}
            />
          </div>
        </header>

        <section className="bg-gray-800/40 border border-gray-700/50 rounded-xl p-4 shadow-inner relative group/section">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-[10px] font-black uppercase tracking-widest text-gray-500">提案内容・理由</h3>
            <button
              onClick={() => {
                if (isSummaryEditing && localSummary !== selectedInsight?.summary) {
                  handleUpdateInsightSummary(selectedInsight?.id, localSummary);
                }
                setIsSummaryEditing(!isSummaryEditing);
              }}
              className="p-1 rounded hover:bg-gray-700 text-gray-500 hover:text-gray-300 transition-all"
              title={isSummaryEditing ? "プレビュー" : "編集"}
            >
              {isSummaryEditing ? (
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
              ) : (
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
              )}
            </button>
          </div>
          <div className="text-gray-300 text-[13px]">
            {isSummaryEditing ? (
              <textarea
                value={localSummary}
                onChange={(e) => setLocalSummary(e.target.value)}
                onBlur={() => {
                  if (localSummary !== selectedInsight?.summary) {
                    handleUpdateInsightSummary(selectedInsight?.id, localSummary);
                  }
                }}
                rows={6}
                className="w-full bg-gray-900/50 border border-gray-700/50 rounded-lg p-3 text-xs font-mono text-gray-200 focus:outline-none focus:border-purple-500/50 leading-relaxed resize-y"
                placeholder="提案内容を入力..."
              />
            ) : (
              <MarkdownBlock text={selectedInsight?.summary} />
            )}
          </div>
	          {isSuggested && isProposedChange && (
	            <div className="mt-4 flex flex-col gap-3 rounded-xl border border-indigo-800/40 bg-indigo-950/30 p-3">
	              <div>
	                <p className="text-xs font-bold text-indigo-200">これは{reviewMeta.label}です</p>
	                <p className="mt-1 text-[11px] leading-relaxed text-indigo-100/80">
	                  {reviewMeta.helperText} {reviewMeta.assetizationReason} 下の編集・適用・却下を使って確認してください。今すぐ処理しない場合は、そのまま閉じれば保留のまま残せます。
	                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  disabled
                  className="inline-flex cursor-not-allowed items-center justify-center gap-2 rounded-lg bg-gray-800 px-3 py-2 text-xs font-bold text-gray-500"
                >
                  Asset候補ではない
                </button>
                <span className="rounded-full border border-indigo-800/50 bg-indigo-950/50 px-2 py-1 text-[10px] font-bold text-indigo-200">
                  使える操作: {reviewMeta.actionSummary}
                </span>
              </div>
            </div>
          )}
          {isSuggested && isHintInsight(selectedInsight) && (
            <div className="mt-4 flex flex-col gap-2 rounded-xl border border-emerald-800/40 bg-emerald-950/30 p-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-xs font-bold text-emerald-200">{isActionHint ? `このHintを${actionTargetLabel}として残す` : 'このHintをAsset化する'}</p>
                <p className="mt-1 text-[11px] text-emerald-100/80">
                  {isActionHint
                    ? `短期メモならNoteのまま残すだけでもOKです。残したい行動だけ${actionTargetLabel}として保存します。今は判断しない場合、そのまま閉じれば保留のまま残せます。`
                    : '内容を確認し、残す価値があるものだけAssetとして保存します。今は判断しない場合、そのまま閉じれば保留のまま残せます。'}
                </p>
              </div>
              <div className="flex flex-col items-end gap-1">
                <button
                  onClick={() => openAssetTypeModal(selectedInsight)}
                  disabled={selectedInsight?.metadata?.parseFailed}
                  className={`inline-flex shrink-0 items-center justify-center gap-2 rounded-lg px-3 py-2 text-xs font-bold transition-all ${
                    selectedInsight?.metadata?.parseFailed
                      ? 'cursor-not-allowed bg-gray-800 text-gray-500'
                      : 'bg-emerald-600 text-white shadow-lg hover:bg-emerald-500 active:scale-95'
                  }`}
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                  </svg>
                  {isActionHint ? `${actionTargetLabel}として残す` : 'Asset化する'}
                </button>
                {selectedInsight?.metadata?.parseFailed && (
                  <p className="text-[10px] text-gray-400">
                    パース失敗のため、このままではAsset化できません。
                  </p>
                )}
              </div>
            </div>
          )}
        </section>

        <section className="bg-gray-800/40 border border-gray-700/50 rounded-xl p-4 shadow-inner space-y-3">
          <h3 className="text-[10px] font-black uppercase tracking-widest text-gray-500 mb-1">文脈</h3>

          <div className="flex items-center gap-2 text-xs h-5">
            <span className="text-gray-500 shrink-0">📄 出典Note:</span>
            {isLoadingRefs ? (
              <div className="w-24 h-3 bg-gray-700 animate-pulse rounded"></div>
            ) : lazySourceNote ? (
              <button
                onClick={() => onReferenceClick?.('note', lazySourceNote.id)}
                className="text-blue-400 hover:underline font-bold truncate flex-1 text-left flex items-center gap-1 min-w-0"
              >
                <span className="truncate">{lazySourceNote.title || '(無題のNote)'}</span>
                <svg className="w-3 h-3 shrink-0 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path></svg>
              </button>
            ) : (
              <span className="text-gray-600 italic">{(selectedInsight?.sourceNoteId || selectedInsight?.sourceNoteIds?.length > 0) ? '(削除済み)' : 'なし'}</span>
            )}
          </div>

          <div className="flex items-center gap-2 text-xs h-5">
            <span className="text-gray-500 shrink-0">🏠 出典Room:</span>
            {isLoadingRefs ? (
              <div className="w-24 h-3 bg-gray-700 animate-pulse rounded"></div>
            ) : lazySourceRoom ? (
              <button
                onClick={() => openSourceRoom?.(lazySourceRoom.id)}
                className="text-blue-400 hover:underline font-bold truncate flex-1 text-left flex items-center gap-1 min-w-0"
              >
                <span className="truncate">{formatRoomName(lazySourceRoom)}</span>
                <svg className="w-3 h-3 shrink-0 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path></svg>
              </button>
            ) : (
              <span className="text-gray-600 italic">{(selectedInsight?.sourceRoomId || selectedInsight?.sourceRoomIds?.length > 0 || lazySourceNote?.sourceRoomId || lazySourceNote?.sourceRoomIds?.length > 0) ? '(削除済み)' : 'なし'}</span>
            )}
          </div>

          {lazyRelatedAssets.length > 0 && (
            <div className="flex items-start gap-2 text-xs">
              <span className="text-gray-500 shrink-0 mt-0.5">🔗 関連Asset:</span>
              <div className="flex flex-wrap gap-x-3 gap-y-1">
                {lazyRelatedAssets.map(asset => (
                  <button
                    key={asset.id}
                    onClick={() => onReferenceClick?.('asset', asset.id)}
                    className="text-emerald-400 hover:underline font-bold flex items-center gap-0.5 min-w-0"
                  >
                    <span className="truncate max-w-[150px]">{asset.title || '(無題のAsset)'}</span>
                    <svg className="w-3 h-3 shrink-0 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path></svg>
                  </button>
                ))}
              </div>
            </div>
          )}
        </section>

        {selectedInsight?.proposedPatch && isProposedChange && (
          isDuplicateProposal ? (
          <section className="bg-amber-950/20 border border-amber-900/40 rounded-xl p-4 shadow-inner">
            <h3 className="text-[10px] font-black uppercase tracking-widest text-amber-400 mb-3">重複候補の確認</h3>
            <div className="space-y-3">
              <div className="p-3 bg-gray-900/60 rounded-lg border border-amber-900/20">
                <p className="text-xs text-amber-200/90">
                  <span className="font-bold">残す内容を整えて</span>、不要な重複候補を片付けます。
                </p>
                <ol className="mt-3 space-y-1.5 text-[11px] text-gray-300 list-decimal list-inside">
                  <li>残す候補を確認します。</li>
                  <li>候補のタグや内容を反映した案を編集します。</li>
                  <li>必要なら案を保存します。</li>
                  <li>不要な候補を{duplicateInsightPreview?.sourceActionLabel}します。</li>
                </ol>
              </div>

              <div className="space-y-3">
                {[
                  {
                    key: 'target',
                    title: '残す候補',
                    accent: 'emerald',
                    item: duplicateInsightPreview?.target,
                    fallbackId: selectedInsight?.targetId,
                  },
                  {
                    key: 'source',
                    title: '参考にする候補',
                    accent: 'amber',
                    item: duplicateInsightPreview?.source,
                    fallbackId: selectedInsight?.proposedPatch?.mergeFrom,
                  },
                ].map(card => {
                  const previewText = getDuplicatePreviewText(card.item, selectedInsight?.targetType);

                  return (
                    <div key={card.key} className="rounded-xl border border-gray-800 bg-gray-950/70 p-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className={`text-[10px] font-black uppercase tracking-widest ${card.accent === 'emerald' ? 'text-emerald-400' : 'text-amber-400'}`}>{card.title}</p>
                          <p className="mt-1 text-sm font-bold text-white truncate">{card.item?.title || card.fallbackId || '(データ未取得)'}</p>
                        </div>
                        {onReferenceClick && card.fallbackId && (
                          <button
                            onClick={() => onReferenceClick(selectedInsight?.targetType, card.fallbackId)}
                            className="px-2 py-1 rounded-md border border-gray-700 text-[10px] font-bold text-gray-300 hover:text-white hover:border-gray-500 transition-colors shrink-0"
                          >
                            開く
                          </button>
                        )}
                      </div>

                      <div className="mt-3 rounded-lg border border-gray-800 bg-gray-900/80 p-3 text-[12px] text-gray-300 whitespace-pre-wrap leading-relaxed max-h-40 overflow-y-auto">
                        {previewText || 'Summaryなし'}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="rounded-xl border border-amber-900/30 bg-gray-950/70 p-3 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h4 className="text-[10px] font-black uppercase tracking-widest text-amber-300">残す内容の編集</h4>
                    <p className="mt-1 text-[11px] leading-relaxed text-gray-400">
                      候補のタグと{duplicateTextLabel}を反映した状態から、不要な部分だけ削って保存できます。
                    </p>
                  </div>
                  {canEditDuplicateTarget && (
                    <div className="flex shrink-0 flex-wrap justify-end gap-1.5">
                      <button
                        onClick={handleResetDuplicateDraft}
                        disabled={duplicateDraftMatchesMerged}
                        className="rounded-md border border-amber-800/50 bg-amber-950/35 px-2 py-1 text-[10px] font-bold text-amber-200 transition-colors hover:bg-amber-900/60 disabled:cursor-not-allowed disabled:border-gray-800 disabled:bg-gray-900/60 disabled:text-gray-600"
                      >
                        もとに戻す
                      </button>
                    </div>
                  )}
                </div>

                {canEditDuplicateTarget ? (
                  <div className="space-y-3">
                    <div className="rounded-lg border border-gray-800 bg-gray-900/80 p-2.5">
                      <div className="mb-2 flex items-center justify-between gap-2">
                        <p className="text-[10px] font-bold uppercase text-gray-500">タグ</p>
                        {duplicateTagReviewItem?.value?.length > 0 && (
                          <span className="rounded-full border border-amber-800/50 bg-amber-950/40 px-2 py-0.5 text-[10px] font-bold text-amber-200">
                            候補タグを反映済み
                          </span>
                        )}
                      </div>
                      <TagEditor
                        tags={duplicateDraftTags}
                        onUpdate={setDuplicateDraftTags}
                        suggestions={tagSuggestions}
                        showToast={showToast}
                      />
                    </div>

                    <div className="rounded-lg border border-gray-800 bg-gray-900/80 p-2.5">
                      <div className="mb-2 flex items-center justify-between gap-2">
                        <p className="text-[10px] font-bold uppercase text-gray-500">{duplicateTextLabel}</p>
                        {duplicateTextReviewItem?.value && (
                          <span className="rounded-full border border-amber-800/50 bg-amber-950/40 px-2 py-0.5 text-[10px] font-bold text-amber-200">
                            候補{duplicateTextLabel}を追記済み
                          </span>
                        )}
                      </div>
                      <textarea
                        value={duplicateDraftSummary}
                        onChange={(e) => setDuplicateDraftSummary(e.target.value)}
                        className="min-h-[120px] w-full resize-y rounded-md border border-gray-700 bg-gray-950 p-2 text-[12px] leading-relaxed text-gray-200 outline-none focus:border-amber-500"
                      />
                    </div>
                  </div>
                ) : duplicateInsightPreview?.reviewItems?.length > 0 ? (
                  <div className="space-y-2">
                    {duplicateInsightPreview.reviewItems.map(item => (
                      <div key={item.label} className="rounded-lg border border-gray-800 bg-gray-900/80 p-2.5">
                        <p className="text-[10px] font-bold text-gray-500 uppercase mb-1">{item.label}</p>
                        {item.kind === 'tags' ? (
                          <div className="flex flex-wrap gap-1.5">
                            {item.value.map(tag => (
                              <span key={tag} className="text-[10px] font-bold text-amber-200 bg-amber-950/60 px-2 py-0.5 rounded-full border border-amber-800/50">
                                #{tag}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <div className="text-[11px] text-gray-300 whitespace-pre-wrap leading-relaxed max-h-32 overflow-y-auto">
                            {item.value}
                          </div>
                        )}
                      </div>
                    ))}
                    <p className="text-[10px] text-gray-500">※ この種類は、この画面では直接編集できません。必要な内容だけ開いて編集してください。</p>
                  </div>
                ) : (
                  <p className="text-[11px] text-gray-400">追加する内容は見つかりませんでした。編集不要なら、そのまま候補を{duplicateInsightPreview?.sourceActionLabel}できます。</p>
                )}
              </div>

            </div>
          </section>
          ) : (
          <section className="bg-indigo-950/20 border border-indigo-900/30 rounded-xl p-4 shadow-inner">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-[10px] font-black uppercase tracking-widest text-indigo-400/80">適用されるパッチ</h3>
              {selectedInsight?.status === 'suggested' && (
                <button
                  onClick={() => {
                    if (isPatchEditing) handleSavePatch();
                    else setIsPatchEditing(true);
                  }}
                  className="p-1 rounded hover:bg-indigo-900/50 text-indigo-400/60 hover:text-indigo-300 transition-all"
                  title={isPatchEditing ? "保存" : "編集"}
                >
                  {isPatchEditing ? (
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" /></svg>
                  ) : (
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                  )}
                </button>
              )}
            </div>

            {selectedInsight?.type === INSIGHT_TYPE.TAG_MERGE && !isPatchEditing ? (
              <div className="flex items-center justify-center gap-4 py-4">
                <div className="flex flex-col items-center gap-1">
                  <span className="text-[8px] font-bold text-gray-500 uppercase tracking-tighter">変更前</span>
                  <span className="bg-gray-800 text-gray-400 border border-gray-700 px-2 py-1 rounded text-xs font-bold line-through opacity-60">
                    #{tagMergeOldTag}
                  </span>
                </div>
                <svg className="w-4 h-4 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                </svg>
                <div className="flex flex-col items-center gap-1">
                  <span className="text-[8px] font-bold text-indigo-400 uppercase tracking-tighter">変更後</span>
                  <span className="bg-indigo-900/40 text-indigo-300 border border-indigo-700/50 px-2 py-1 rounded text-xs font-bold shadow-lg shadow-indigo-900/20">
                    #{tagMergeNewTag}
                  </span>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {Object.entries(isPatchEditing ? localPatch : selectedInsight.proposedPatch).map(([key, value]) => (
                  <div key={key} className="flex flex-col gap-1.5">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-bold text-indigo-300 bg-indigo-900/40 px-1.5 py-0.5 rounded border border-indigo-800/50 uppercase tracking-tighter">
                        {PATCH_FIELD_LABELS[key] || key}
                      </span>
                    </div>
                    <div className="bg-gray-900/80 border border-gray-800 p-2.5 rounded-lg">
                      {isPatchEditing ? (
                        Array.isArray(value) ? (
                          <TagEditor
                            tags={value}
                            onUpdate={(newTags) => handleUpdatePatchField(key, newTags)}
                            suggestions={tagSuggestions}
                            showToast={showToast}
                          />
                        ) : typeof value === 'string' && (key === 'body' || key === 'summary') ? (
                          <textarea
                            value={value}
                            onChange={(e) => handleUpdatePatchField(key, e.target.value)}
                            className="w-full bg-transparent text-[11px] text-gray-300 font-mono focus:outline-none resize-none leading-relaxed"
                            rows={Math.max(3, value.split('\n').length)}
                          />
                        ) : (
                          <input
                            type="text"
                            value={value}
                            onChange={(e) => handleUpdatePatchField(key, e.target.value)}
                            className="w-full bg-transparent text-[11px] text-gray-300 font-mono focus:outline-none"
                          />
                        )
                      ) : (
                        Array.isArray(value) ? (
                          <div className="flex flex-wrap gap-1.5">
                            {value.map(v => <span key={v} className="text-[10px] font-bold text-gray-400 bg-gray-800 px-2 py-0.5 rounded-full border border-gray-700">#{v}</span>)}
                            {value.length === 0 && <span className="text-[10px] italic text-gray-600">empty</span>}
                          </div>
                        ) : typeof value === 'string' ? (
                          <div className="text-[11px] text-gray-300 font-mono whitespace-pre-wrap leading-relaxed">{value}</div>
                        ) : (
                          <div className="text-[11px] text-gray-300 font-mono">{JSON.stringify(value)}</div>
                        )
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {isPatchEditing && (
              <div className="mt-4 flex justify-end gap-2">
                <button
                  onClick={() => { setIsPatchEditing(false); setLocalPatch(selectedInsight.proposedPatch); }}
                  className="px-3 py-1 rounded bg-gray-800 text-[10px] font-bold text-gray-400 hover:text-white transition-all"
                >
                  キャンセル
                </button>
                <button
                  onClick={handleSavePatch}
                  className="px-3 py-1 rounded bg-indigo-900/50 text-[10px] font-bold text-indigo-300 hover:bg-indigo-800 hover:text-white border border-indigo-800/50 transition-all"
                >
                  パッチを更新
                </button>
              </div>
            )}
            {selectedInsight?.status === 'suggested' && (
              <div className="mt-4 flex flex-col gap-2 rounded-xl border border-indigo-800/40 bg-indigo-950/30 p-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-xs font-bold text-indigo-200">この提案を適用する</p>
                  <p className="mt-1 text-[11px] text-indigo-100/80">
                    表示中のパッチを対象データへ反映します。
                  </p>
                </div>
                <button
                  onClick={() => onAcceptProposal(selectedInsight)}
                  disabled={isAcceptingProposal}
                  className={`inline-flex shrink-0 items-center justify-center gap-2 rounded-lg px-3 py-2 text-xs font-bold transition-all ${
                    isAcceptingProposal
                      ? 'cursor-not-allowed bg-gray-800 text-gray-500'
                      : 'bg-indigo-600 text-white shadow-lg hover:bg-indigo-500 active:scale-95'
                  }`}
                >
                  {isAcceptingProposal ? (
                    <svg className="h-3.5 w-3.5 animate-spin" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                  ) : (
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                  適用する
                </button>
              </div>
            )}
          </section>
          )
        )}

        {selectedInsight?.status === 'rejected' && (
          <section className="bg-red-950/20 border border-red-900/30 rounded-xl p-4 shadow-inner">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-[10px] font-black uppercase tracking-widest text-red-400/80">却下理由</h3>
              {!isEditingMemo && (
                <button
                  onClick={() => setIsEditingMemo(true)}
                  className="text-[10px] font-bold text-red-400/60 hover:text-red-300 transition-colors"
                >
                  編集
                </button>
              )}
            </div>
            {isEditingMemo ? (
              <div className="space-y-2">
                <textarea
                  value={editingMemo}
                  onChange={(e) => setEditingMemo(e.target.value)}
                  className="w-full bg-gray-900 border border-red-900/50 text-gray-200 p-2 rounded-lg text-xs focus:outline-none focus:border-red-500 h-24 shadow-inner"
                  placeholder="却下理由を入力してください..."
                  autoFocus
                />
                <div className="flex gap-2 justify-end">
                  <button
                    onClick={() => { setIsEditingMemo(false); setEditingMemo(rejectionMemo); }}
                    className="px-3 py-1 rounded bg-gray-800 text-[10px] font-bold text-gray-400 hover:text-white transition-all"
                  >
                    キャンセル
                  </button>
                  <button
                    onClick={handleSaveMemo}
                    className="px-3 py-1 rounded bg-red-900/50 text-[10px] font-bold text-red-300 hover:bg-red-800 hover:text-white border border-red-800/50 transition-all"
                  >
                    保存
                  </button>
                </div>
              </div>
            ) : (
              <div
                className="text-xs text-gray-300 leading-relaxed cursor-pointer hover:text-white transition-colors"
                onClick={() => setIsEditingMemo(true)}
              >
                {rejectionMemo ? (
                  <p>{rejectionMemo}</p>
                ) : (
                  <p className="text-gray-500 italic">理由なし（タップして入力）</p>
                )}
              </div>
            )}
          </section>
        )}

      </article>
      {showDuplicateActionBar && (
        <div className="fixed inset-x-0 bottom-[calc(60px+env(safe-area-inset-bottom))] z-[90] border-t border-amber-900/40 bg-gray-950/95 px-4 py-3 shadow-[0_-16px_30px_rgba(0,0,0,0.35)] backdrop-blur lg:sticky lg:inset-x-auto lg:bottom-0 lg:z-20 lg:-mx-4 lg:mt-2">
          <p className="text-[11px] leading-relaxed text-amber-100/80">
            {duplicateTargetHasChanges
              ? '編集が未保存です。保存してから候補を片付けてください。'
              : '上の編集欄で提案を修正できます。'}
          </p>
          <div className="mt-2 grid grid-cols-2 gap-2">
            {duplicateTargetHasChanges && canEditDuplicateTarget && (
              <button
                onClick={handleSaveDuplicateTarget}
                disabled={isSavingDuplicateTarget}
                className="col-span-2 inline-flex min-h-10 items-center justify-center rounded-lg border border-amber-700 bg-amber-950/70 px-3 py-2 text-xs font-bold text-amber-100 transition-colors hover:bg-amber-900/80 disabled:cursor-not-allowed disabled:border-gray-700 disabled:bg-gray-800 disabled:text-gray-500"
              >
                {isSavingDuplicateTarget ? '保存中...' : '保存'}
              </button>
            )}
            <button
              onClick={() => { setRejectingInsightId(selectedInsight?.id); setRejectReason(''); setIsRejectModalOpen(true); }}
              disabled={duplicateTargetHasChanges || isSavingDuplicateTarget}
              className={`inline-flex min-h-10 items-center justify-center rounded-lg border px-3 py-2 text-xs font-bold leading-tight transition-colors ${
                duplicateTargetHasChanges || isSavingDuplicateTarget
                  ? 'cursor-not-allowed border-gray-700 bg-gray-800 text-gray-500'
                  : 'border-red-800/60 bg-red-950/35 text-red-200 hover:border-red-600 hover:bg-red-900/60 hover:text-white'
              }`}
            >
              提案を却下
            </button>
            <button
              onClick={() => handleAcceptDuplicateProposal(selectedInsight)}
              disabled={isProcessingDuplicate || duplicateTargetHasChanges || isSavingDuplicateTarget}
              className={`inline-flex min-h-10 items-center justify-center rounded-lg px-3 py-2 text-xs font-bold transition-all ${
                isProcessingDuplicate || duplicateTargetHasChanges || isSavingDuplicateTarget
                  ? 'cursor-not-allowed border border-gray-700 bg-gray-800 text-gray-500'
                  : 'bg-amber-600 text-white shadow-lg hover:bg-amber-500 active:scale-95'
              }`}
            >
              {isProcessingDuplicate ? '処理中...' : getDuplicateActionButtonLabel(duplicateInsightPreview?.sourceActionLabel, 'confirm')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
